import { useEffect, useRef, useState } from "react";
import { Fish } from "lucide-react";
import { theme, btnPrimary, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { PortalLink } from "../../components/PortalLink";
import { SheetCard, DimensionHeading } from "../../components/Drawing";
import { SeverityPill, secondaryBtn, textarea, fmtDay } from "./shared";

// Training proposed from phishing results (backend lib/phishingTrainingProposals.ts). A department
// whose phishing incidents or simulation results cross the organisation's operating parameters is
// offered a course. "Review and assign" opens the ordinary assignment form pre-filled; nothing is
// assigned until it is submitted. A dismissal needs a reason and is kept on record.

export interface ProposalAudience { all: boolean; departmentIds: string[]; siteIds: string[]; userIds: string[] }

export interface PhishingProposal {
  id: string;
  departmentId: string;
  departmentName: string;
  memberCount: number;
  since: string;
  sinceBasis: "lookback" | "assignment" | "dismissal";
  triggers: Array<"incidents" | "click_rate">;
  incidents: { count: number; refs: Array<{ id: string; ref: string }> };
  simulations: { count: number; clicked: number; reported: number; clickRate: number | null };
  latestEvidenceAt: string;
  severity: "high" | "medium";
  reasons: string[];
  suggested: { title: string; kind: string; description: string; dueInDays: number; audience: ProposalAudience };
}

export interface ProposalView {
  thresholds: { lookbackMonths: number; incidentThreshold: number; clickRatePercent: number; minSimulations: number };
  proposals: PhishingProposal[];
  dismissed: Array<{ id: string; departmentId: string; departmentName: string; reason: string; dismissedAt: string; dismissedByName: string | null }>;
}

export function PhishingTrainingProposals({ view, canEdit, focusDepartmentId, onAssign, onChanged }: {
  view: ProposalView;
  canEdit: boolean;
  /** ?proposal=<departmentId>, from the My Actions item. */
  focusDepartmentId: string | null;
  onAssign: (p: PhishingProposal) => void;
  onChanged: (notice: string) => Promise<void>;
}) {
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const focusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { focusRef.current?.scrollIntoView({ block: "center" }); }, [focusDepartmentId]);

  if (view.proposals.length === 0 && view.dismissed.length === 0) return null;
  const t = view.thresholds;

  async function dismiss(p: PhishingProposal) {
    if (reason.trim().length < 3) { setError("Give a reason of at least three characters."); return; }
    setBusy(true); setError(null);
    try {
      await api.post(`/training-assignments/proposals/${encodeURIComponent(p.departmentId)}/dismiss`, { reason: reason.trim() });
      setDismissing(null); setReason("");
      await onChanged(`Proposal for ${p.departmentName} dismissed. It comes back only if new phishing results arrive.`);
    } catch (e) { setError(apiErrorMessage(e, "Could not dismiss the proposal")); }
    finally { setBusy(false); }
  }

  async function restore(id: string, name: string) {
    setBusy(true); setError(null);
    try {
      await api.post(`/training-assignments/proposals/dismissals/${encodeURIComponent(id)}/withdraw`, {});
      await onChanged(`Dismissal withdrawn for ${name}.`);
    } catch (e) { setError(apiErrorMessage(e, "Could not withdraw the dismissal")); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <DimensionHeading as="h3" count={view.proposals.length}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Fish size={15} /> Proposed from phishing results</span>
      </DimensionHeading>
      <div style={{ ...text.hint, marginTop: -4, marginBottom: 10, maxWidth: 720 }}>
        A department is proposed training when, over the last {t.lookbackMonths} month{t.lookbackMonths === 1 ? "" : "s"}, {t.incidentThreshold} or more phishing or social engineering incident{t.incidentThreshold === 1 ? " is" : "s are"} filed against it, or its simulation click rate reaches {t.clickRatePercent}% across at least {t.minSimulations} simulations.
        Change these under <PortalLink to="/operating-parameters">Operating parameters</PortalLink>. Nothing is assigned until you confirm it.
      </div>
      {error && <div style={{ ...text.body, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>}
      <div style={{ display: "grid", gap: 10 }}>
        {view.proposals.map((p) => {
          const focused = p.departmentId === focusDepartmentId;
          return (
            <div key={p.id} ref={focused ? focusRef : undefined}>
              <SheetCard
                featured={focused}
                label={`Training for ${p.departmentName}`}
                reference={`${p.memberCount} ${p.memberCount === 1 ? "member" : "members"}`}
                actions={<SeverityPill value={p.severity} />}
              >
                <ul style={{ ...text.body, margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
                  {p.reasons.map((r) => <li key={r}>{r}</li>)}
                </ul>
                {p.incidents.refs.length > 0 && (
                  <div style={{ ...text.meta, marginTop: 6 }}>
                    Incidents:{" "}
                    {p.incidents.refs.map((r, i) => (
                      <span key={r.id}>{i > 0 && ", "}<PortalLink to={`/resilience/incidents?incident=${encodeURIComponent(r.id)}`} style={{ ...text.ref }}>{r.ref}</PortalLink></span>
                    ))}
                  </div>
                )}
                <div style={{ ...text.meta, marginTop: 6 }}>
                  Proposed course: {p.suggested.title}, due in {p.suggested.dueInDays} days, assigned to {p.departmentName}. You can change any of it before assigning.
                </div>
                {canEdit && dismissing !== p.id && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    <button type="button" style={btnPrimary} disabled={busy} onClick={() => onAssign(p)}>Review and assign</button>
                    <button type="button" style={secondaryBtn} disabled={busy} onClick={() => { setDismissing(p.id); setReason(""); setError(null); }}>Dismiss</button>
                  </div>
                )}
                {canEdit && dismissing === p.id && (
                  <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                    <label style={text.label} htmlFor={`dismiss-${p.id}`}>Why is no training needed?</label>
                    <textarea id={`dismiss-${p.id}`} rows={2} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} style={textarea}
                      placeholder="For example, the department completed a phishing workshop last month" />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button" style={btnPrimary} disabled={busy || reason.trim().length < 3} onClick={() => void dismiss(p)}>{busy ? "Dismissing…" : "Dismiss proposal"}</button>
                      <button type="button" style={secondaryBtn} disabled={busy} onClick={() => setDismissing(null)}>Cancel</button>
                    </div>
                  </div>
                )}
              </SheetCard>
            </div>
          );
        })}
      </div>
      {view.dismissed.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ ...text.meta, cursor: "pointer" }}>Dismissed proposals ({view.dismissed.length})</summary>
          <ul style={{ ...text.body, margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 6 }}>
            {view.dismissed.map((d) => (
              <li key={d.id}>
                <strong>{d.departmentName}</strong>, {fmtDay(d.dismissedAt)}{d.dismissedByName ? ` by ${d.dismissedByName}` : ""}: {d.reason}
                {canEdit && <button type="button" style={{ ...secondaryBtn, marginLeft: 8, padding: "2px 8px" }} disabled={busy} onClick={() => void restore(d.id, d.departmentName)}>Withdraw dismissal</button>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
